import { getDemoScrollContainers, getDemoScrollDestination } from './scroll-geometry'
import { centerDemoField, trackDemoField } from './center-field'
import { getDemoPlayerClock, waitForDemoPlayback } from './player-clock'
import { CSSProperties } from 'react'

import {
  DEMO_CLICK_DELAY,
  DEMO_CUSTOMS_READING_DURATION,
  DEMO_CUSTOMS_READING_SCROLL_DELAY,
  DEMO_CUSTOMS_READING_SCROLL_DISTANCE,
  DEMO_SCROLL_DELAY,
  DEMO_SCROLL_SPEED,
} from './playback-constants'

const preparedFields = new WeakMap<HTMLInputElement | HTMLTextAreaElement, AnimatedField>()

export const wait = waitForDemoPlayback

export const waitForStableElementPosition = async (
  element: HTMLElement,
  isCancelled: () => boolean,
): Promise<boolean> => {
  let previousScrollTop = window.scrollY
  let previousTop = element.getBoundingClientRect().top
  let stableMeasurements = 0

  while (!isCancelled()) {
    await wait(50)

    const currentScrollTop = window.scrollY
    const currentTop = element.getBoundingClientRect().top
    const scrollIsStable = Math.abs(currentScrollTop - previousScrollTop) < 1
    const elementIsStable = Math.abs(currentTop - previousTop) < 1

    if (scrollIsStable && elementIsStable) {
      stableMeasurements += 1
    } else {
      stableMeasurements = 0
    }

    if (stableMeasurements === 3) {
      return true
    }

    previousScrollTop = currentScrollTop
    previousTop = currentTop
  }

  return false
}

export const animateAction = async (action: HTMLElement, shouldScroll = true) => {
  if (shouldScroll) {
    if (!centerDemoField(action) && !action.closest('.demo-player')) {
      action.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
    await wait(DEMO_SCROLL_DELAY)
  }

  if (action.closest('.demo-player')) {
    for (let attempt = 0; attempt < 6; attempt += 1) {
      centerDemoField(action)
      await wait(50)
    }
  }

  const stopTracking = action.closest('.demo-player') ? trackDemoField(action) : () => {}
  try {
    if (action.closest('.demo-player')) {
      const clock = getDemoPlayerClock()
      const stable = await waitForStableElementPosition(action, () => (
        !action.isConnected || Boolean(clock?.controller.signal.aborted)
      ))
      if (!stable) throw new DOMException('Playback cancelled', 'AbortError')
    }

    action.classList.add('calculator__demo-step-button_playback-click')
    await wait(DEMO_CLICK_DELAY)
    if (action.closest('.demo-player')) centerDemoField(action)
  } finally {
    stopTracking()
    action.classList.remove('calculator__demo-step-button_playback-click')
  }
}

export const animateDemoContentScroll = ({
  content,
  getDestination,
  duration,
  isCancelled,
}: DemoContentScrollParams): Promise<boolean> => new Promise(resolve => {
  const clock = getDemoPlayerClock()
  const signal = clock?.controller.signal
  let start = content.scrollTop
  let destination = getDestination()
  let elapsed = 0
  let previous = performance.now()
  let wasInterrupted = false
  let frame: number

  const finish = (reachedDestination: boolean) => {
    cancelAnimationFrame(frame)
    signal?.removeEventListener('abort', abort)
    resolve(reachedDestination)
  }
  const abort = () => finish(false)
  const tick = (now: number) => {
    const playbackCancelled = isCancelled() || signal?.aborted

    if (playbackCancelled || !content.isConnected) {
      finish(false)
      return
    }

    const delta = now - previous
    previous = now

    if (clock?.paused || (clock && now < clock.manualScrollUntil)) {
      wasInterrupted = true
      frame = requestAnimationFrame(tick)
      return
    }

    if (wasInterrupted) {
      start = content.scrollTop
      destination = getDestination()
      elapsed = 0
      wasInterrupted = false
    }

    elapsed = Math.min(duration, elapsed + delta * (clock?.speed || 1) * DEMO_SCROLL_SPEED)
    const progress = elapsed / duration
    const easedProgress = progress * progress * (3 - 2 * progress)
    content.scrollTop = start + (destination - start) * easedProgress

    if (progress >= 1) {
      finish(true)
      return
    }

    frame = requestAnimationFrame(tick)
  }

  signal?.addEventListener('abort', abort, { once: true })
  frame = requestAnimationFrame(tick)
})

export const scrollToDemoPlayerElement = async ({
  element,
  isCancelled,
}: DemoScrollToElementParams): Promise<boolean> => {
  const containers = getDemoScrollContainers(element)
  if (!element.closest('.demo-player')) return false

  for (const content of containers) {
    const getDestination = () => getDemoScrollDestination({ element, content })
    const distance = Math.abs(getDestination() - content.scrollTop)
    const duration = Math.max(DEMO_SCROLL_DELAY, Math.min(DEMO_CUSTOMS_READING_DURATION, distance / .6))
    if (!await animateDemoContentScroll({
      content,
      getDestination,
      duration,
      isCancelled: () => isCancelled() || !element.isConnected,
    })) return false
  }

  return centerDemoField(element)
}

export const animateDemoPlayerAction = async ({ action, isCancelled }: DemoPlayerActionParams) => {
  const scrolledInPlayer = await scrollToDemoPlayerElement({ element: action, isCancelled })

  if (isCancelled()) {
    throw new DOMException('Playback cancelled', 'AbortError')
  }

  await animateAction(action, !scrolledInPlayer)
}

export const scrollToCustomsAction = async (
  action: HTMLElement,
  isCancelled: () => boolean,
): Promise<boolean> => {
  if (action.closest('.demo-player')) {
    return scrollToDemoPlayerElement({ element: action, isCancelled })
  }

  if (centerDemoField(action)) {
    return !isCancelled()
  }

  while (!isCancelled()) {
    const distanceToCenter = action.getBoundingClientRect().top - window.innerHeight / 2

    if (distanceToCenter <= 0) {
      return true
    }

    const previousScrollTop = window.scrollY

    window.scrollTo({
      behavior: 'smooth',
      top: window.scrollY + Math.min(DEMO_CUSTOMS_READING_SCROLL_DISTANCE, distanceToCenter),
    })
    await wait(DEMO_CUSTOMS_READING_SCROLL_DELAY)

    if (Math.abs(window.scrollY - previousScrollTop) < 1) {
      return true
    }
  }

  return false
}

export const waitForStepElement = async (
  step: DemoCalculatorStep,
  isCancelled: () => boolean,
): Promise<HTMLElement | null> => {
  while (!isCancelled()) {
    const stepElement = document.querySelector<HTMLElement>(`[data-calculator-step="${step}"]`)
    const collapse = stepElement?.querySelector<HTMLElement>('.MuiCollapse-root')
    const stepIsExpanded = !collapse || collapse.classList.contains('MuiCollapse-entered')

    if (stepElement && stepIsExpanded) {
      return stepElement
    }

    await wait(100)
  }

  return null
}

export const waitForDocumentElement = async (
  selector: string,
  isCancelled: () => boolean,
): Promise<HTMLElement | null> => {
  while (!isCancelled()) {
    const element = document.querySelector<HTMLElement>(selector)

    if (element) {
      return element
    }

    await wait(100)
  }

  return null
}

export const waitForDocumentElementToDisappear = async (
  selector: string,
  isCancelled: () => boolean,
): Promise<boolean> => {
  while (!isCancelled()) {
    if (!document.querySelector(selector)) {
      return true
    }

    await wait(100)
  }

  return false
}

export const waitForEnabledDocumentElement = async (
  selector: string,
  isCancelled: () => boolean,
): Promise<HTMLElement | null> => {
  while (!isCancelled()) {
    const element = document.querySelector<HTMLElement>(selector)

    if (!element) {
      await wait(100)
      continue
    }

    const isEnabled = !(element instanceof HTMLButtonElement) || !element.disabled

    if (isEnabled) {
      return element
    }

    await wait(100)
  }

  return null
}

export const waitForStepData = async (
  stepElement: HTMLElement,
  isCancelled: () => boolean,
): Promise<boolean> => {
  while (!isCancelled()) {
    const fields = Array.from(
      stepElement.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea'),
    )

    if (fields.some(field => isTypeableField(field) && field.getClientRects().length > 0)) {
      return true
    }

    await wait(100)
  }

  return false
}

const isTypeableField = (
  element: HTMLInputElement | HTMLTextAreaElement,
) => {
  const computedStyle = window.getComputedStyle(element)
  const isHiddenByAttribute = element.getAttribute('aria-hidden') === 'true'
  const isHiddenByStyle = computedStyle.display === 'none' ||
    computedStyle.visibility === 'hidden'
  const isVisuallyHidden = isHiddenByAttribute || isHiddenByStyle

  if (isVisuallyHidden) {
    return false
  }

  if (element.tagName === 'TEXTAREA') {
    return Boolean(element.value)
  }

  return ![ 'checkbox', 'hidden', 'radio' ].includes(element.type) && Boolean(element.value)
}

const getOverlayStyle = ({ element, root }: DemoFieldOverlayStyleParams): CSSProperties => {
  const elementRect = element.getBoundingClientRect()
  const rootRect = root.getBoundingClientRect()
  const canvas = root.closest<HTMLElement>('.demo-calculator-canvas, .demo-calculator-overlays')
  const scale = canvas ? canvas.getBoundingClientRect().width / canvas.offsetWidth : 1
  const computedStyle = window.getComputedStyle(element)
  const paddingLeft = Number.parseFloat(computedStyle.paddingLeft) || 0
  const paddingTop = Number.parseFloat(computedStyle.paddingTop) || 0
  const lineHeight = Number.parseFloat(computedStyle.lineHeight) || Number.parseFloat(computedStyle.fontSize)
  const isTextArea = element.tagName === 'TEXTAREA'
  // The portal shares the canvas zoom; its CSS position uses logical coordinates.
  const elementTop = (elementRect.top - rootRect.top) / scale - root.clientTop
  const verticalCenter = elementTop + elementRect.height / scale / 2
  const firstLineCenter = elementTop + paddingTop + lineHeight / 2

  let top = verticalCenter

  if (isTextArea) {
    top = firstLineCenter
  }

  return {
    color: computedStyle.getPropertyValue('-webkit-text-fill-color') || computedStyle.color,
    fontFamily: computedStyle.fontFamily,
    fontSize: computedStyle.fontSize,
    fontWeight: computedStyle.fontWeight,
    left: (elementRect.left - rootRect.left) / scale - root.clientLeft + paddingLeft,
    letterSpacing: computedStyle.letterSpacing,
    lineHeight: computedStyle.lineHeight,
    top,
  }
}

export const refreshFieldOverlayStyle = (field: AnimatedField) => {
  if (field.element.getClientRects().length === 0) {
    return
  }

  const nextOverlayStyle = getOverlayStyle({ element: field.element, root: field.root })

  field.overlayStyle = {
    ...field.overlayStyle,
    left: nextOverlayStyle.left,
    top: nextOverlayStyle.top,
  }
}

export const prepareField = (
  element: HTMLInputElement | HTMLTextAreaElement,
  targetValue: string,
): AnimatedField | null => {
  const preparedField = preparedFields.get(element)

  if (preparedField) {
    return preparedField
  }

  const root = element.closest<HTMLElement>('.MuiInputBase-root')

  if (!root || element.getClientRects().length === 0) {
    return null
  }

  const previousCaretColor = element.style.caretColor
  const previousColor = element.style.color
  const previousTextFillColor = element.style.getPropertyValue('-webkit-text-fill-color')
  const overlayStyle = getOverlayStyle({ element, root })

  root.classList.add('calculator-demo-typing-field')
  element.style.caretColor = 'transparent'
  element.style.color = 'transparent'
  element.style.setProperty('-webkit-text-fill-color', 'transparent')

  const field = {
    element,
    overlayStyle,
    previousCaretColor,
    previousColor,
    previousTextFillColor,
    root,
    targetValue,
    visibleValue: '',
  }

  preparedFields.set(element, field)

  return field
}

export const prepareFields = (stepElement: HTMLElement): AnimatedField[] => (
  Array.from(stepElement.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('input, textarea'))
    .filter(isTypeableField)
    .reduce<AnimatedField[]>((fields, element) => {
      const field = prepareField(element, element.value)

      if (field) {
        fields.push(field)
      }

      return fields
    }, [])
)

export const restoreFields = (fields: AnimatedField[]) => {
  fields.forEach((field) => {
    const {
      element,
      previousCaretColor,
      previousColor,
      previousTextFillColor,
      root,
    } = field

    root.classList.remove('calculator-demo-typing-field')
    element.style.caretColor = previousCaretColor
    element.style.color = previousColor

    if (previousTextFillColor) {
      element.style.setProperty('-webkit-text-fill-color', previousTextFillColor)
    } else {
      element.style.removeProperty('-webkit-text-fill-color')
    }

    if (preparedFields.get(element) === field) {
      preparedFields.delete(element)
    }
  })
}

export const restoreStaticValues = (values: StaticValueState[]) => {
  values.forEach(({ element, opacity, transition }) => {
    element.style.opacity = opacity
    element.style.transition = transition
  })
}
